import React from 'react';
import { styled } from '../../Themes';
import { focusRing, focusRingNoGap } from '../../Themes/base/focusRing';
import {
	borderColor,
	borderRadius,
	borderStyle,
	borderWidth,
	marginSize,
	paddingSize,
} from '../../Utilities/utils/Theming';
import { Badge } from '../Badge';
import { MIcon } from '../Icon';
import { IDummyProps } from './types';

export const rootStyle = () => {
	return {
		fontSize: 'var(--epods--font-size-p)',
		fontFamily: 'var(--epods--font-base)',
		color: 'var(--epods--color-text-primary)',
		zIndex: 'var(--epods--zindex-dropdown)',
	};
};

export const dropdownStatusStyle = (
	$error: boolean,
	$positive: boolean,
	$isFocused: boolean,
	$disabled: boolean,
	$theme: any,
	readOnly: any,
) => {
	const focusCrtl = $isFocused
		? {
			backgroundColor: 'var(--epods--color-bg-input)',
			...focusRingNoGap,
			...borderColor('transparent !important'),
		}
		: {};

	const errorCrtl = $error
		? {
			backgroundColor: 'var(--epods--color-signal-error-bg)',
			...borderColor('var(--epods--color-signal-error-border)'),
		}
		: {};

	const positiveCrtl = $positive
		? {
			backgroundColor: 'var(--epods--color-signal-success-bg)',
			...borderColor('var(--epods--color-signal-success-border)'),
		}
		: {};

	const readonlyCtrl = readOnly
		? {
			backgroundColor: 'var(--epods--color-bg-input-readonly)',
			...borderColor('var(--epods--color-border)'),
		}
		: {};

	const defaultCrtl = {
		backgroundColor: 'var(--epods--color-bg-input)',
		...borderColor($disabled ? 'var(--epods--color-border-light)' : 'var(--epods--color-border)'),
		':hover': {
			...borderColor(
				$disabled ? 'var(--epods--color-border-light)' : 'var(--epods--color-border-active)',
			),
		},
	};

	return {
		...{
			...borderRadius('var(--epods--border-radius)'),
			...borderWidth('var(--epods--border-width)'),
		},
		...defaultCrtl,
		...positiveCrtl,
		...errorCrtl,
		...readonlyCtrl,
		...focusCrtl,
	};
};

export const InputDefaultSize = {
	lineHeight: 'var(--epods--dropdown-input-size-default)',
	height: 'var(--epods--dropdown-input-size-default)',
};

/**
 * SPACING
 */
export const inputSizesStyle = (props: any) => {
	const _size = props.size || 'default';
	return {
		fontSize: 'var(--epods--dropdown-' + _size + '-font-size)',
		fontFamily: 'var(--epods--font-base)',
		height: 'auto',
		color: 'var(--epods--color-text-primary)',
	};
};

export const inputPresetMultiStyle = (props: any) => {
	let hideInput = {};
	if (props.preset === 'multi' || props.preset === 'multi-tags') {
		hideInput = {
			height: '0px',
			width: '0px',
			position: 'absolute',
		};
	}
	return hideInput;
};
// dropdownInputBadge
export const valueContainerInputBadge = (_props: IDummyProps) => {
	if (_props.badge) {
		return {
			display: 'flex',
			justifyContent: _props.multi ? 'space-between' : 'flex-end',
			flexDirection: 'row-reverse',
		};
	} else {
		return {};
	}
};

export const defaultPaddingContainerStyles = {
	minHeight: 'calc(var(--epods--dropdown-height-default) - 2px)',
	paddingTop: 'calc((var(--epods--dropdown-height-default) - var(--epods--dropdown-input-size-default) - 2px) / 2)',
	paddingBottom:
		'calc((var(--epods--dropdown-height-default) - var(--epods--dropdown-input-size-default) - 2px) / 2)',
	paddingLeft: 'var(--epods--dropdown-padding-sides)',
};

// dropdownPaddingComboStyles
export const valueContainerPaddingStyles = ($theme: any, props: IDummyProps) => {
	const _size = props.size || 'default';
	return {
		minHeight: 'var(--epods--dropdown-' + _size + '-height)',
		alignContent: 'center',
		paddingTop: '0',
		paddingBottom: '0',
		paddingLeft:
			props.type === 'search'
				? 'var(--epods--dropdown-padding-left-icon)'
				: 'var(--epods--dropdown-padding-sides)',
	};
};

/**
 * CLOSE ICON
 */
export const clearIconStyle = () => {
	return {
		position: 'relative',
		zIndex: 2,
		paddingRight: '18px', // Trick to hide the original icon
	};
};

/**
 * DROPDOWN
 */
export const popOverBodyStyle = () => {
	return {
		// width: '900px',
		zIndex: 100,
		marginTop: 'var(--epods--space-inset-s)',
		boxShadow: 'var(--epods--box-shadow-third-bottom)',
		...borderRadius('var(--epods--border-radius)'),
		...borderStyle("solid"),
		...borderWidth("var(--epods--border-width)"),
		...borderColor("var(--epods--color-border-light)"),
		backgroundColor: 'var(--epods--color-bg-third-elevation)',
		overflow: 'hidden',
		...paddingSize(
			'var(--epods--dropdown-popover-padding-v)',
			'0',
			'calc(var(--epods--dropdown-popover-padding-v) - 5px)',
			'0',
		),
	};
};

export const optionContentStyle = ($theme: any, $selected: any, $isHighlighted: any, props: any) => {
	return {
		...marginSize('0', '0', '5px', '0'),
		...paddingSize(
			'var(--epods--dropdown-popover-option-padding-v)',
			'var(--epods--dropdown-popover-option-padding-h)',
			'var(--epods--dropdown-popover-option-padding-v)',
			'var(--epods--dropdown-popover-option-padding-h)',
		),
		height:
			props.size === 'large'
				? 'var(--epods--list-item-large-height)'
				: props.size === 'compact'
					? 'var(--epods--list-item-compact-height)'
					: props.size === 'mini'
						? 'var(--epods--list-item-mini-height)'
						: 'var(--epods--list-item-default-height)',
		display: 'flex',
		alignContent: 'center',
		fontFamily: $selected ? 'var(--epods--font-base-semibold)' : 'var(--epods--font-base)',
		fontWeight: '100',
		marginLeft: 'var(--epods--dropdown-popover-padding-h)',
		marginRight: 'var(--epods--dropdown-popover-padding-h)',
		fontSize:
			props.size === 'compact'
				? 'var(--epods--dropdown-compact-font-size)'
				: props.size === 'large'
					? 'var(--epods--dropdown-large-font-size)'
					: props.size === 'mini'
						? 'var(--epods--dropdown-mini-font-size)'
						: 'var(--epods--dropdown-default-font-size)',
		color: 'var(--epods--color-text-primary)',
		...borderWidth('var(--epods--border-width)'),
		...borderStyle('solid'),
		...borderColor($isHighlighted ? 'var(--epods--color-border-active)' : 'transparent'),
		backgroundColor: $selected && props.preset != 'multi' ? 'var(--epods--color-bg-active)' : 'transparent',
		...borderRadius('var(--epods--border-radius)'),
		span: {
			fontFamily: 'inherit',
		},
	};
};

/**
 * TAGS OVERRIDES
 */

export const tagOverrideDropdown = (props: any) => {
	const tagBorderColor = (disabled: boolean) => {
		if (props.readOnly) {
			return borderColor('var(--epods--dropdown-multi-tag-readonly-border-color)');
		}
		if (disabled) {
			return borderColor('var(--epods--dropdown-multi-tag-disabled-border-color)');
		}
		return borderColor('var(--epods--dropdown-multi-tag-border-color)');
	};

	const _fontSize =
		props.size === 'compact'
			? 'var(--epods--tag-compact-font-size)'
			: props.size === 'large'
				? 'var(--epods--tag-default-font-size)'
				: props.size === 'mini'
					? 'var(--epods--tag-compact-font-size)'
					: 'var(--epods--tag-default-font-size)';

	const _height =
		props.size === 'compact'
			? 'var(--epods--tag-compact-height)'
			: props.size === 'large'
				? 'var(--epods--tag-default-height)'
				: props.size === 'mini'
					? 'var(--epods--tag-compact-height)'
					: 'var(--epods--tag-default-height)';

	return {
		props: {
			overrides: {
				Root: {
					props: {
						tabIndex: -1,
						role: 'presentation',
						"aria-label": "tag",
						"data-epo": "epo-dropdown-tag-override"
					},
					style: ({ $theme, $disabled }: any) => {
						return {
							paddingRight: $disabled ? '8px' : '0',
							paddingLeft: '8px',
							paddingTop: props.size === 'mini' || props.size === 'compact' ? '0' : '1px',
							paddingBottom: props.size === 'mini' || props.size === 'compact' ? '0' : '1px',
							...borderRadius('var(--epods--dropdown-multi-tag-border-radius)'),
							...borderWidth('1px'),
							...tagBorderColor($disabled),
							color: props.readOnly
								? 'var(--epods--color-text-tag)'
								: $disabled
									? 'var(--epods--color-text-tag-disabled)'
									: 'var(--epods--color-text-tag)',
							backgroundColor: props.readOnly
								? 'var(--epods--color-bg-tag-indigo)'
								: $disabled
									? 'var(--epods--color-bg-tag-disabled)'
									: 'var(--epods--color-bg-tag-indigo)',
							display: props.preset == 'multi' ? 'none' : 'inherit',
							marginTop: '0',
							marginBottom: '0',
							marginLeft: '0',
							':focus': {
								//boxShadow: '0 0 0 2px var(--epods--color-border-focus)',
								...focusRing
							},
							height: _height,
						};
					},
				},
				Action: {
					props: {
						tabIndex: 0,
						role: "button",
						onKeyPress: (e: any) => { e.stopPropagation(); e.target.click(); },
						"aria-hidden": "false",
						"aria-label": "Remove"
					},
					style: ({ $theme, $disabled }: any) => {
						return {
							display: $disabled ? 'none' : 'inherit',
							borderBottomLeftRadius: '0',
							borderBottomRightRadius: 'calc(var(--epods--dropdown-multi-tag-border-radius) - 1px)',
							borderTopRightRadius: 'calc(var(--epods--dropdown-multi-tag-border-radius) - 1px)',
							borderTopLeftRadius: '0',
							paddingRight: '8px',
							paddingLeft: '3px',
							marginLeft: '2px',
							color: 'var(--epods--color-text-tag)',
							':hover': {
								backgroundColor: props.readOnly
									? 'inherit'
									: 'var(--epods--color-bg-tag-indigo-hover)',
							},
							':active': {
								backgroundColor: props.readOnly
									? 'inherit'
									: 'var(--epods--color-bg-tag-indigo-active)',
							},
							':selected': {
								backgroundColor: props.readOnly
									? 'inherit'
									: 'var(--epods--color-bg-tag-indigo-active)',
							},
							':focus': {
								backgroundColor: props.readOnly
									? 'inherit'
									: 'var(--epods--color-bg-tag-indigo-active)',
								...focusRing
							},
							height: _height,
						};
					},
				},
				Text: {
					style: {
						fontFamily: 'var(--epods--font-base)',
						lineHeight: props.size === 'mini' || props.size === 'compact' ? 'var(--epods--tag-compact-line-height)' : 'var(--epods--tag-default-line-height)',
						fontSize: _fontSize,
					},
				},
				ActionIcon: {
					component: () => (
						<MIcon
							icon="close"
							size={props.size === 'mini' ? '15px' : props.size === 'compact' ? '16px' : 'small'}
							color="var(--epods--color-text-primary)"
						/>
					),
				},
			},
		},
	};
};

/**
 * CONFIGURATION FOR THE MULTI WITH BAGDE: FROM PRESETS
 */
export const multiSumaryDropdown = (props: any, css: any) => {
	if (props.preset === 'multi') {
		return {
			component: (_props: any) => {
				const { children, ...restProps } = _props;
				return (
					<div
						data-epo="epo-dropdown-multi-summary"
						className={css({
							display: 'flex',
							justifyContent: 'space-between',
							flex: '1',
							...inputSizesStyle(props),
						})}
						onClick={(e: any) => { return true }}
					>
						{props.value?.length > 0 && (
							<span className={css({ color: 'var(--epods--color-text-primary)' })}>
								{props.textMultiSummary}
							</span>
						)}
						{props.value?.length === 0 && (
							<span className={css({ color: 'var(--epods--color-text-disabled)' })}>
								{props.placeholder}
							</span>
						)}
						{props.preset === 'multi' && (
							<div>
								<Badge className="ml-xs" theme="grey" size={props.size}>
									{props.value?.length ? props.value?.length : 0}
								</Badge>
							</div>
						)}
						{children}
					</div>
				);
			},
		};
	}
	return {};
};

export const OptionLabel = styled('span', () => {
	return {
		fontFamily: 'inherit',
		flex: '1 1 auto',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	};
});
